<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import store from "@/store";

import { toast } from "@/common/utils/toast";

import {
  formQuestionList,
  formQuestionCreate,
  formQuestionUpdate,
  formQuestionDelete,
} from "@/common/api/rest/form/question";

import type {
  FormQuestionPayloadType,
  FormQuestionType,
} from "@/common/types/form/question";

import { questionForm } from "@/components";

import { baseConfirm, baseModal, baseTable } from "@/common/components";

const props = defineProps<{
  accountId: string;
  formId: string;
  orders?: string[];
  remove?: string[];
  ordering?: boolean;
}>();

const emit = defineEmits(["update:order"]);

const { t, tm } = useI18n();

const listData = ref<FormQuestionType[]>([]);

const selected = ref<FormQuestionType | null>(null);
const questionPayload = ref<FormQuestionPayloadType | null>(null);

const isEdit = ref(false);
const isLoading = ref(false);
const showQuestionAdd = ref(false);
const showQuestionDel = ref(false);

const tableHeader = computed(() => [
  { key: "name", label: t("common.labels.name") },
  { key: "type", label: t("common.labels.type") },
  { key: "required", label: t("common.labels.required") },
]);

const modalMessage = computed(() => {
  return isEdit.value
    ? t("question.texts.edit_question").toUpperCase()
    : t("question.texts.add_question").toUpperCase();
});

const delMessage = computed(() => {
  const message = t("question.texts.del_confirm", {
    concat: selected.value?.name,
  });
  const undo = t("common.texts.undo_message");

  return `${message}<br>${undo}`;
});

const questionTypes = computed(
  () => tm("enums.question_type") as Record<string, string>,
);

function sortData(data: FormQuestionType[]) {
  return data?.sort((a, b) => {
    const indexA = props.orders?.indexOf(a.question_id);
    const indexB = props.orders?.indexOf(b.question_id);

    return indexA - indexB;
  });
}

function fetchData() {
  isLoading.value = true;

  formQuestionList(props.accountId, props.formId, {
    full: true,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        if (props.ordering && props.orders) {
          listData.value = sortData(data.list);
        } else {
          listData.value = data.list;
        }
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      isLoading.value = false;
    });
}

function add() {
  showQuestionAdd.value = true;
}

function addCancel() {
  isEdit.value = false;
  selected.value = null;
  questionPayload.value = null;
  showQuestionAdd.value = false;
}

function addConfirm() {
  if (!questionPayload.value) return;

  store.commit("SET_LOADING", true);

  formQuestionCreate(props.accountId, props.formId, questionPayload.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        const newOrders = [...listData.value];
        newOrders.push(data);
        updateOrder(newOrders);

        fetchData();
        addCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function edit(data: FormQuestionType) {
  questionPayload.value = data;
  selected.value = data;
  isEdit.value = true;
  showQuestionAdd.value = true;
}

function editConfirm() {
  if (!questionPayload.value) return;

  store.commit("SET_LOADING", true);

  formQuestionUpdate(
    props.accountId,
    selected.value?.question_id as string,
    questionPayload.value,
  )
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        addCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch((error) => {
      console.log("error", error);
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function questionSubmit() {
  if (isEdit.value) editConfirm();
  else addConfirm();
}

function del(data: FormQuestionType) {
  selected.value = data;
  showQuestionDel.value = true;
}

function delCancel() {
  selected.value = null;
  showQuestionDel.value = false;
}

function delConfirm() {
  store.commit("SET_LOADING", true);

  formQuestionDelete(props.accountId, selected.value?.question_id as string)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        delCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function updateOrder(data: FormQuestionType[]) {
  const newOrder = data.map((item) => item.question_id);
  emit("update:order", newOrder);
}

onMounted(() => {
  fetchData();
});
</script>
<template>
  <section class="question-tab">
    <div class="title">
      {{ t("common.labels.questions") }}
    </div>

    <base-table
      :header="tableHeader"
      :data="listData"
      :loading="isLoading"
      :options="true"
      :edit="true"
      :del="true"
      :paginated="false"
      :show-search="false"
      :ordering="ordering"
      @btn:add="add()"
      @btn:edit="edit($event)"
      @btn:del="del($event)"
      @update:order="updateOrder"
    >
      <template #type="{ data }">
        {{ questionTypes[data.type] }}
      </template>

      <template #required="{ data }">
        <template v-if="data.required">
          {{ t("common.labels.yes") }}
        </template>
        <template v-else>
          {{ t("common.labels.not") }}
        </template>
      </template>
    </base-table>

    <base-modal
      width="80%"
      :show="showQuestionAdd"
      :show-button-confirm="true"
      :button-confirm="t('common.labels.confirm')"
      :button-close="t('common.labels.cancel')"
      :title="modalMessage"
      @close="addCancel()"
      @confirm="questionSubmit()"
    >
      <template #content>
        <question-form
          width="100%"
          :data="questionPayload"
          :remove="remove"
          @update-data="questionPayload = $event"
        />
      </template>
    </base-modal>

    <base-confirm
      max-width="500px"
      :show="showQuestionDel"
      :title="t('question.texts.del_question').toUpperCase()"
      :message="delMessage"
      @cancel="delCancel()"
      @confirm="delConfirm()"
    />
  </section>
</template>
<style lang="scss" scoped>
.question-tab {
  display: flex;
  flex-direction: column;

  .title {
    display: flex;
    flex-direction: column;
    padding-inline: 0.5rem;
    border-bottom: 1px solid var(--border);
    margin-block: 2rem;
    padding-block-end: 0.5rem;
    font-size: 1.15rem;
    font-weight: bold;
    text-transform: uppercase;
  }
}
</style>
